/**
 * Admin Booking Module — /app/booking
 * Voir réservations + confirmer/annuler + gérer horaires instructeurs.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Calendar, Phone, Mail, Check, X, Loader2, RefreshCw, Users, Link as LinkIcon, Rss, Copy } from "lucide-react";

const CAD = (v) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v || 0);

const STATUS_BADGE = {
  pending: { label: "⏳ En attente", color: "bg-amber-500/20 text-amber-300" },
  confirmed: { label: "✓ Confirmé", color: "bg-emerald-500/20 text-emerald-300" },
  cancelled: { label: "✗ Annulé", color: "bg-red-500/20 text-red-300" },
  completed: { label: "✓ Terminé", color: "bg-zinc-700 text-zinc-300" },
  no_show: { label: "🚫 No show", color: "bg-red-500/30 text-red-200" },
  reschedule_proposed: { label: "🔄 Report proposé", color: "bg-amber-500/20 text-amber-300" },
};


export default function BookingAdminModule() {
  const [reservations, setReservations] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [filterInstructor, setFilterInstructor] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedInfo, setFeedInfo] = useState(null);
  const [weeklyPost, setWeeklyPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);

  const genWeeklyPost = async () => {
    setLoadingPost(true);
    try {
      const r = await api.get(`/booking/weekly-post`);
      setWeeklyPost(r.data);
      toast.success(`Post généré · ${r.data.total_slots} places dispo`);
    } catch (e) {
      toast.error("Erreur génération post");
    } finally {
      setLoadingPost(false);
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  const loadFeed = async () => {
    try {
      const base = window.location.origin;
      const r = await api.get(`/booking/feed-url?request_url_base=${encodeURIComponent(base)}`);
      setFeedInfo(r.data);
    } catch (e) {
      toast.error("Erreur feed URL");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterInstructor) params.set("instructor_id", filterInstructor);
      const [r, i] = await Promise.all([
        api.get(`/booking/reservations?${params}`),
        api.get(`/booking/instructors`),
      ]);
      setReservations(r.data.reservations);
      setInstructors(i.data.instructors);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterInstructor]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (booking_id, status) => {
    try {
      await api.patch(`/booking/reservations/${booking_id}`, { status });
      toast.success("Mis à jour");
      load();
    } catch (e) {
      toast.error("Erreur mise à jour");
    }
  };

  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [newSlot, setNewSlot] = useState("");
  const [rescheduleMsg, setRescheduleMsg] = useState("");
  const [reschBusy, setReschBusy] = useState(false);
  const submitReschedule = async () => {
    if (!newSlot) { toast.error("Choisis une nouvelle date/heure."); return; }
    setReschBusy(true);
    try {
      const iso = new Date(newSlot).toISOString();
      await api.post(`/booking/reservations/${rescheduleFor.id}/reschedule`,
        { new_start_at: iso, message: rescheduleMsg || undefined });
      toast.success("Nouvelle plage proposée au client (courriel + SMS envoyés) 📨");
      setRescheduleFor(null); setNewSlot(""); setRescheduleMsg(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la proposition."); }
    finally { setReschBusy(false); }
  };

  const publicUrl = `${window.location.origin}/reserver-prive`;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="booking-admin-module">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-amber-100 tracking-tight flex items-center gap-2">
            <Calendar className="w-7 h-7 text-amber-400" /> Réservations Privées
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Cours 1-on-1 avec Ralph, Sonia ou Karl · Vue admin · Confirmer/annuler en 1 clic.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={publicUrl} target="_blank" rel="noopener noreferrer"
             className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-semibold flex items-center gap-2"
             data-testid="public-link-btn">
            <LinkIcon className="w-4 h-4" /> Page publique
          </a>
          <button onClick={genWeeklyPost} disabled={loadingPost}
            className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
            data-testid="gen-weekly-post-btn">
            {loadingPost ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rss className="w-4 h-4" />}
            Post hebdo dispos
          </button>
          <button onClick={load} className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-amber-400">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Public URL share */}
      <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-zinc-950 p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-amber-400">URL publique de réservation</div>
          <code className="text-amber-100 font-mono text-sm">{publicUrl}</code>
        </div>
        <button
          onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("URL copiée"); }}
          className="px-3 py-1.5 rounded border border-amber-500/40 text-amber-300 text-xs hover:bg-amber-500/10"
          data-testid="copy-url-btn"
        >Copier</button>
      </div>

      {/* Post hebdo dispos privés (généré) */}
      {weeklyPost && (
        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-zinc-950 p-4 space-y-3" data-testid="weekly-post-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-widest text-emerald-400 flex items-center gap-1">
              <Rss className="w-3 h-3" /> Post hebdo — {weeklyPost.total_slots} places dispo
            </div>
          </div>
          {[
            { key: "post_fr", label: "Post FR (Facebook / courriel)", rows: 8 },
            { key: "post_en", label: "Post EN", rows: 8 },
            { key: "sms_fr", label: "SMS FR", rows: 2 },
            { key: "sms_en", label: "SMS EN", rows: 2 },
          ].map(({ key, label, rows }) => (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-zinc-300">{label}</span>
                <button onClick={() => copyText(weeklyPost[key], label)}
                  className="px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 text-xs hover:bg-emerald-500/10 flex items-center gap-1"
                  data-testid={`copy-${key}-btn`}>
                  <Copy className="w-3 h-3" /> Copier
                </button>
              </div>
              <textarea readOnly value={weeklyPost[key]} rows={rows}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-2 text-xs text-zinc-200 font-mono resize-none" />
            </div>
          ))}
        </div>
      )}


      {/* Calendar Subscription Feed */}
      <div className="rounded-2xl border border-blue-500/30 bg-gradient-to-r from-blue-500/5 to-zinc-950 p-4" data-testid="feed-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-blue-400 flex items-center gap-1">
              <Rss className="w-3 h-3" /> Abonnement Calendrier (anti-doublon)
            </div>
            <div className="text-sm text-zinc-300 mt-1">
              S'abonne UNE FOIS dans Google/Apple/Outlook avec l'URL ci-dessous.
              Toutes tes réservations KAIZEN apparaîtront automatiquement (sync quotidienne).
              Plus besoin de gérer 3 agendas en parallèle.
            </div>
            {!feedInfo ? (
              <button
                onClick={loadFeed}
                className="mt-3 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-400 text-zinc-950 text-xs font-semibold"
                data-testid="get-feed-btn"
              >
                Générer mon URL d'abonnement
              </button>
            ) : (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 bg-zinc-900 rounded p-2">
                  <code className="text-blue-300 text-[11px] font-mono flex-1 break-all">{feedInfo.feed_url}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(feedInfo.feed_url); toast.success("URL copiée"); }}
                    className="p-1.5 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-300"
                    data-testid="copy-feed-btn"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <details className="text-xs text-zinc-400">
                  <summary className="cursor-pointer hover:text-blue-300">📖 Comment l'ajouter</summary>
                  <div className="mt-2 pl-3 space-y-1.5">
                    <div><strong className="text-amber-300">Google Calendar :</strong> {feedInfo.instructions.google}</div>
                    <div><strong className="text-amber-300">Apple Calendar (Mac) :</strong> {feedInfo.instructions.apple}</div>
                    <div><strong className="text-amber-300">iPhone :</strong> {feedInfo.instructions.iphone}</div>
                    <div><strong className="text-amber-300">Outlook :</strong> {feedInfo.instructions.outlook}</div>
                  </div>
                </details>
                <div className="text-[10px] text-blue-400 italic">{feedInfo.tip}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap text-xs">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 text-amber-100 rounded px-3 py-1.5"
                data-testid="filter-status">
          <option value="">Tous statuts</option>
          <option value="pending">⏳ En attente</option>
          <option value="confirmed">✓ Confirmés</option>
          <option value="cancelled">✗ Annulés</option>
          <option value="completed">Terminés</option>
        </select>
        <select value={filterInstructor} onChange={(e) => setFilterInstructor(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 text-amber-100 rounded px-3 py-1.5"
                data-testid="filter-instructor">
          <option value="">Tous instructeurs</option>
          {instructors.map(i => (
            <option key={i.id} value={i.id}>{i.full_name}</option>
          ))}
        </select>
      </div>

      {/* Reservations */}
      <div className="space-y-2" data-testid="reservations-list">
        {reservations.length === 0 && !loading && (
          <div className="text-center text-zinc-500 py-12">
            <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>Aucune réservation dans ce filtre.</p>
            <p className="text-xs mt-1">Partage l'URL publique ci-dessus pour recevoir tes premiers bookings.</p>
          </div>
        )}
        {reservations.map((r) => {
          const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
          const startDt = new Date(r.start_at);
          return (
            <div key={r.id} className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4 hover:bg-zinc-900/50" data-testid={`res-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[300px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-amber-100">{r.client_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded ${badge.color}`}>{badge.label}</span>
                    <span className="text-xs text-zinc-400">avec <strong className="text-amber-300">{r.instructor_name}</strong></span>
                  </div>
                  <div className="text-sm text-zinc-300 mt-1">
                    📅 {startDt.toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" })}
                    {" · "}🕐 {startDt.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}
                    {" · "}{r.duration_minutes}min
                    {r.discipline && <span> · {r.discipline}</span>}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-zinc-400 mt-1">
                    {r.client_email && <a href={`mailto:${r.client_email}`} className="flex items-center gap-1 hover:text-amber-300"><Mail className="w-3 h-3" />{r.client_email}</a>}
                    {r.client_phone && <a href={`tel:${r.client_phone}`} className="flex items-center gap-1 hover:text-amber-300"><Phone className="w-3 h-3" />{r.client_phone}</a>}
                    {r.credit_applied ? <span className="text-emerald-300" data-testid={`credit-badge-${r.id}`}>🎟️ {r.price_note || "Couvert par forfait"}{r.credit_settled ? ` · ${r.credit_settled === "utiliser" ? "séance comptée" : "crédit libéré"}` : ""}</span> : <span className="text-emerald-300">{CAD(r.estimated_price)}</span>}
                  </div>
                  {r.notes && <div className="text-xs text-zinc-400 mt-1 italic">📝 {r.notes}</div>}
                </div>
                <div className="flex gap-1.5">
                  {r.status !== "confirmed" && (
                    <button onClick={() => updateStatus(r.id, "confirmed")}
                            className="px-2.5 py-1 rounded text-xs border bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20"
                            data-testid={`confirm-${r.id}`}>
                      <Check className="w-3 h-3 inline" /> Confirmer
                    </button>
                  )}
                  {r.status !== "cancelled" && (
                    <button onClick={() => updateStatus(r.id, "cancelled")}
                            className="px-2.5 py-1 rounded text-xs border bg-red-500/10 text-red-300 border-red-500/30 hover:bg-red-500/20"
                            data-testid={`cancel-${r.id}`}>
                      <X className="w-3 h-3 inline" /> Annuler
                    </button>
                  )}
                  {r.status !== "cancelled" && (
                    <button onClick={() => { setRescheduleFor(r); setNewSlot(""); setRescheduleMsg(""); }}
                            className="px-2.5 py-1 rounded text-xs border bg-amber-500/10 text-amber-300 border-amber-500/30 hover:bg-amber-500/20"
                            data-testid={`reschedule-${r.id}`}>
                      <RefreshCw className="w-3 h-3 inline" /> Reporter
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-zinc-500 text-center border-t border-zinc-800 pt-3">
        Instructeurs configurés : {instructors.map(i => i.full_name).join(" · ")} · Pour modifier les horaires, vois <code>POST /api/booking/instructors/{`{id}`}/rules</code>
      </div>

      {rescheduleFor && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="reschedule-modal">
          <div className="bg-zinc-950 border border-amber-700/40 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-amber-300 flex items-center gap-2"><RefreshCw className="w-5 h-5" /> Proposer une nouvelle plage</h3>
            <p className="text-sm text-zinc-400 mt-1">Client : <b>{rescheduleFor.client_name}</b> — on lui enverra un courriel + SMS avec un lien pour accepter en 1 clic.</p>
            <label className="block text-xs uppercase tracking-wide text-zinc-500 mt-4 mb-1">Nouvelle date et heure</label>
            <input type="datetime-local" value={newSlot} onChange={(e) => setNewSlot(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" data-testid="reschedule-datetime" />
            <label className="block text-xs uppercase tracking-wide text-zinc-500 mt-3 mb-1">Message au client (optionnel)</label>
            <textarea value={rescheduleMsg} onChange={(e) => setRescheduleMsg(e.target.value)} rows={2}
              placeholder="Ex : Empêchement de dernière minute, désolé !"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setRescheduleFor(null)} className="flex-1 border border-zinc-700 text-zinc-300 rounded-lg py-2 text-sm">Annuler</button>
              <button onClick={submitReschedule} disabled={reschBusy} data-testid="submit-reschedule-btn"
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-black font-semibold rounded-lg py-2 text-sm disabled:opacity-60">
                {reschBusy ? "Envoi…" : "Proposer au client"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
